/**
 * AssetDetailSheet — Detail-Ansicht einer Anlage
 *
 * Zeigt KPIs, AfA-Plan (Timeline + Postings-Tabelle), Verknüpfungen, Aktionen.
 */
import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Lock, ArrowRightLeft, Trash2, ExternalLink, Loader2, Calendar, Edit3 } from "lucide-react";
import api from "@/utils/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import Paragraph15aEditDialog from "./Paragraph15aEditDialog";

const STATUS_COLORS = {
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  disposed: "bg-zinc-100 text-zinc-600 border-zinc-200",
  written_off: "bg-amber-100 text-amber-700 border-amber-200",
};

const METHOD_LABELS = {
  linear: "Linear",
  degressive: "Degressiv",
  pool_5y: "Sammelposten 5J",
  gwg_immediate: "GWG Sofort",
};

export default function AssetDetailSheet({ assetId, open, onClose, onUpdated, onDispose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editPrivOpen, setEditPrivOpen] = useState(false);

  const reload = () => {
    if (!assetId) return;
    setLoading(true);
    api
      .get(`/fixed-assets/${assetId}`)
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open && assetId) reload();
  }, [open, assetId]);  // eslint-disable-line

  const handleDelete = async () => {
    if (!window.confirm(`Anlage „${data.asset.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/fixed-assets/${data.asset.id}`);
      toast.success("Anlage gelöscht");
      onUpdated?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const asset = data?.asset;
  const postings = data?.postings || [];
  const totalAfa = postings.reduce((s, p) => s + (p.amount || 0), 0);
  const currentYear = new Date().getFullYear();
  // Buchwert per Jahresende des aktuellen Jahres (= Bewertung zum heutigen Tag, vereinfacht)
  const currentPosting = postings.find((p) => p.fiscal_year === currentYear);
  const bookValue = currentPosting
    ? currentPosting.book_value_after
    : postings.length
      ? postings[0].book_value_before ?? asset?.cost_net ?? 0
      : asset?.cost_net ?? 0;
  const ytd = postings
    .filter((p) => p.fiscal_year === currentYear)
    .reduce((s, p) => s + p.amount, 0);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent className="w-full sm:max-w-[640px] overflow-y-auto" data-testid="asset-detail-sheet">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade…
          </div>
        ) : asset ? (
          <>
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                {asset.name}
                <Badge variant="outline" className={STATUS_COLORS[asset.status]}>
                  {asset.status === "active" ? "Aktiv" : asset.status === "disposed" ? "Veräußert" : "Abgeschrieben"}
                </Badge>
                {asset.locked_by_advisor && (
                  <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200" data-testid="locked-badge">
                    <Lock className="h-3 w-3 mr-1" /> SB-gesperrt
                  </Badge>
                )}
                {asset.locked_by_year_closure && (
                  <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">
                    <Lock className="h-3 w-3 mr-1" /> Jahresabschluss {asset.locked_by_year_closure}
                  </Badge>
                )}
              </SheetTitle>
            </SheetHeader>

            <div className="space-y-4 pt-4 pb-8">
              {/* SB-Lock-Banner */}
              {asset.locked_by_advisor && (
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-800">
                  <strong>🔒 Vom Steuerberater festgeschrieben.</strong> Änderungen sind nur durch deinen SB möglich.
                </div>
              )}

              {/* KPI Mini-Cards */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <div className="text-xs text-slate-500">Anschaffungskosten</div>
                  <div className="text-lg font-semibold tabular-nums">{asset.cost_net?.toFixed(2)} €</div>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                  <div className="text-xs text-emerald-600">Aktueller Buchwert</div>
                  <div className="text-lg font-semibold text-emerald-700 tabular-nums">{bookValue.toFixed(2)} €</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <div className="text-xs text-slate-500">Kum. AfA</div>
                  <div className="text-lg font-semibold tabular-nums">{(asset.cost_net - bookValue).toFixed(2)} €</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <div className="text-xs text-slate-500">AfA dieses Jahr</div>
                  <div className="text-lg font-semibold tabular-nums">{ytd.toFixed(2)} €</div>
                </div>
              </div>

              {/* Stammdaten */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600 uppercase tracking-wider">Stammdaten</div>
                <div className="p-3 space-y-1.5 text-sm">
                  <Row label="Kategorie" value={asset.category_key} />
                  <Row label="Anschaffung" value={<span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{asset.acquisition_date}</span>} />
                  <Row label="AfA-Beginn" value={asset.depreciation_start_date} />
                  <Row label="Nutzungsdauer" value={`${asset.useful_life_years} Jahre`} />
                  <Row label="Methode" value={METHOD_LABELS[asset.depreciation_method] || asset.depreciation_method} />
                  {asset.private_use_percentage > 0 && (
                    <Row label="Privat-Anteil" value={
                      <span className="flex items-center gap-1">
                        {asset.private_use_percentage} %
                        {asset.status === "active" && !asset.locked_by_advisor && !asset.locked_by_year_closure && (
                          <button
                            onClick={() => setEditPrivOpen(true)}
                            className="text-emerald-600 hover:text-emerald-800"
                            data-testid="edit-private-pct-btn"
                            title="Privat-Anteil ändern (§ 15a)"
                          >
                            <Edit3 className="h-3 w-3" />
                          </button>
                        )}
                      </span>
                    } highlight="amber" />
                  )}
                  {asset.private_use_percentage === 0 && asset.status === "active" && !asset.locked_by_advisor && !asset.locked_by_year_closure && (
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-400">Privat-Anteil: 0 %</span>
                      <button
                        onClick={() => setEditPrivOpen(true)}
                        className="text-emerald-600 hover:underline"
                        data-testid="add-private-pct-btn"
                      >
                        Privat-Anteil hinzufügen
                      </button>
                    </div>
                  )}
                  {asset.vendor && <Row label="Lieferant" value={asset.vendor} />}
                  {asset.receipt_file_key && (
                    <Row label="Beleg" value={
                      <button
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem("auth_token") || "";
                            const url = `${process.env.REACT_APP_BACKEND_URL}/api/fixed-assets/${asset.id}/receipt`;
                            const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                            if (!r.ok) throw new Error(r.statusText);
                            const blob = await r.blob();
                            const u = URL.createObjectURL(blob);
                            window.open(u, "_blank");
                            setTimeout(() => URL.revokeObjectURL(u), 60_000);
                          } catch (e) {
                            toast.error("Beleg-Download fehlgeschlagen");
                          }
                        }}
                        className="text-emerald-600 hover:underline flex items-center gap-1"
                        data-testid="download-asset-receipt-btn"
                      >
                        {asset.receipt_filename || "Beleg öffnen"} <ExternalLink className="h-3 w-3" />
                      </button>
                    } />
                  )}
                  {asset.source_expense_id && (
                    <Row label="Original-Beleg" value={
                      <Link to={`/expense/${asset.source_expense_id}`} className="text-emerald-600 hover:underline flex items-center gap-1">
                        Anzeigen <ExternalLink className="h-3 w-3" />
                      </Link>
                    } />
                  )}
                </div>
              </div>

              {/* AfA-Plan */}
              <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="afa-postings-table">
                <div className="bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600 uppercase tracking-wider flex items-center justify-between">
                  <span>AfA-Plan ({postings.length} Buchungen)</span>
                  <span className="text-emerald-700">Σ {totalAfa.toFixed(2)} €</span>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-white text-xs text-slate-500">
                    <tr>
                      <th className="px-3 py-1.5 text-left">Jahr</th>
                      <th className="px-3 py-1.5 text-right">AfA</th>
                      <th className="px-3 py-1.5 text-right">Buchwert</th>
                      <th className="px-3 py-1.5 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {postings.map((p) => (
                      <tr key={p.id} className="border-t border-slate-100">
                        <td className="px-3 py-1.5 font-medium">{p.fiscal_year}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{p.amount?.toFixed(2)} €</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{p.book_value_after?.toFixed(2)} €</td>
                        <td className="px-3 py-1.5">
                          {p.status === "posted" ? (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Gebucht</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">Geplant</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* § 15a-Log falls vorhanden */}
              {asset.paragraph_15a_log && asset.paragraph_15a_log.length > 0 && (
                <div className="border border-amber-200 rounded-lg overflow-hidden">
                  <div className="bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800 uppercase tracking-wider">
                    § 15a UStG-Korrekturen
                  </div>
                  <div className="p-3 space-y-1.5 text-xs">
                    {asset.paragraph_15a_log.map((e, i) => (
                      <div key={i} className="flex justify-between border-b border-amber-100 last:border-0 pb-1">
                        <span>{e.event_date} · {e.old_private_pct}% → {e.new_private_pct}%</span>
                        <span className="tabular-nums font-medium">
                          {e.correction_amount_eur >= 0 ? "+" : ""}{e.correction_amount_eur.toFixed(2)} €
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Disposal-Info falls disposed */}
              {asset.status === "disposed" && (
                <div className="border border-zinc-200 rounded-lg p-3 bg-zinc-50">
                  <div className="text-xs font-medium text-zinc-600 uppercase tracking-wider mb-2">Veräußerung</div>
                  <Row label="Datum" value={asset.disposal_date} />
                  <Row label="Art" value={asset.disposal_type} />
                  <Row label="Erlös brutto" value={`${asset.disposal_proceeds_gross?.toFixed(2)} €`} />
                  <Row label="Restbuchwert" value={`${asset.disposal_book_value_at_date?.toFixed(2)} €`} />
                  <Row label="Gewinn/Verlust" value={
                    <span className={asset.disposal_gain_loss >= 0 ? "text-emerald-700 font-medium" : "text-rose-700 font-medium"}>
                      {asset.disposal_gain_loss >= 0 ? "+" : ""}{asset.disposal_gain_loss?.toFixed(2)} €
                    </span>
                  } />
                </div>
              )}

              {/* Aktionen */}
              {asset.status === "active" && !asset.locked_by_advisor && !asset.locked_by_year_closure && (
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className="flex-1" onClick={() => onDispose?.(asset)} data-testid="dispose-btn">
                    <ArrowRightLeft className="h-4 w-4 mr-2" />
                    Veräußern
                  </Button>
                  <Button variant="outline" className="text-rose-600 hover:bg-rose-50" onClick={handleDelete} data-testid="delete-asset-btn">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="py-20 text-center text-slate-500">Anlage nicht gefunden</div>
        )}
        {/* § 15a-Edit-Dialog */}
        <Paragraph15aEditDialog
          open={editPrivOpen}
          onClose={() => setEditPrivOpen(false)}
          asset={asset}
          onUpdated={() => { reload(); onUpdated?.(); }}
        />
      </SheetContent>
    </Sheet>
  );
}

function Row({ label, value, highlight }) {
  const bg = highlight === "amber" ? "bg-amber-50 -mx-3 px-3 rounded" : "";
  return (
    <div className={`flex justify-between gap-2 ${bg}`}>
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-800 font-medium text-right">{value}</span>
    </div>
  );
}
